import useSlingApi from '@sdk/api/useSlingApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';

import { TaskStatuses } from '@web/shiftTasks/types/TaskStatuses';
import { getShiftTaskDetailsQueryKey } from '@web/shiftTasks/utils/getQueryKey';
import { SubtaskInstance } from '@web/shiftTasks/types/SubtaskIntance';

import { useShiftTaskInstancesQueryCache } from './useShiftTasksInstancesQueryCache';

type UpdateSubtaskMutationPayload = {
  subtask: SubtaskInstance;
  status: TaskStatuses;
};

export function useUpdateSubtaskStatus(taskId: ID, onCompleteSubtask: VoidFunction) {
  const { shiftTasks } = useSlingApi();
  const queryClient = useQueryClient();
  const { updateSubtaskInstanceStatus } = useShiftTaskInstancesQueryCache();
  const queryKey = getShiftTaskDetailsQueryKey(taskId);
  const { showErrorToast } = useToast();

  const mutation = useMutation({
    mutationFn: async ({ subtask, status }: UpdateSubtaskMutationPayload) => {
      const result = await shiftTasks.updateSubtaskStatus(subtask.id, status);
      return result.data;
    },
    onMutate: async ({ subtask, status }) => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      const previousSubtask = subtask;
      updateSubtaskInstanceStatus(taskId, { ...subtask, status });

      return { previousSubtask };
    },
    onSuccess() {
      onCompleteSubtask();
    },
    onError(error, payload, { previousSubtask }) {
      console.log({ error });
      updateSubtaskInstanceStatus(taskId, previousSubtask);
      showErrorToast(getErrorForToast(error));
    },
  });

  return mutation;
}
